import { InterchangeError, readJavaJson } from '../tables/jsonReader.ts';
import builtInText from '../../../../../schema/fixtures/sheet-templates.json?raw';
import { templateFrom, type TemplateData } from './sheetCodec.ts';
import { sheetTemplate, type SheetTemplate } from './sheetModel.ts';

/**
 * The built-in character sheet templates (5th edition, Fate Accelerated, Forged in the Dark
 * and the rest), read from schema/fixtures/sheet-templates.json - which the desktop's
 * `SheetCasesTest` writes from its `BuiltInTemplates` - so their fields and their licences'
 * attributions are never retyped (docs/PARITY-STEP-2-PLAN.md decision 5). As on the desktop,
 * a realm gets its own copy of one the first time a sheet uses it.
 */

export interface BuiltInTemplate extends TemplateData {
  /** `srd-5.1-character`, `fate-accelerated`... */
  readonly key: string;
}

function load(): BuiltInTemplate[] {
  const document = readJavaJson(builtInText);
  const templates = document instanceof Map ? document.get('templates') : null;
  if (!Array.isArray(templates)) throw new InterchangeError('sheet-templates.json has no "templates" list.');
  return templates.map((template) => {
    if (!(template instanceof Map)) throw new InterchangeError('A built-in template must be a JSON object.');
    const data = templateFrom(template);
    return { ...data, key: data.builtinKey };
  });
}

let all: BuiltInTemplate[] | null = null;

/** Every built-in, in the desktop's order. */
export function builtInTemplates(): readonly BuiltInTemplate[] {
  all ??= load();
  return all;
}

export const findBuiltIn = (key: string): BuiltInTemplate | null => builtInTemplates().find((b) => b.key === key) ?? null;

/** `BuiltIn.toTemplate()`: a realm's own copy, which remembers the built-in it came from. */
export function builtInToTemplate(builtIn: BuiltInTemplate, id: string = crypto.randomUUID(), now: string = new Date().toISOString()): SheetTemplate {
  return sheetTemplate(id, builtIn, now, now);
}
